import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Icon, api, naira } from "../lib/core";

const POLICY = {
  intro: "Refunds are only considered when you accidentally fund your wallet with an excess amount. We refund only unused excess funds, back to the same bank account you paid from.",
  eligible: [
    "The refund is for excess wallet funding you did not intend to make",
    "The funds are still unused (available) in your wallet",
    "The request is verified against your original funding transaction",
    "Funds are returned to the originating bank account only — never a third party",
  ],
  docs: [
    "Proof of the original bank payment (bank alert or statement entry)",
    "The bank account details the payment came from (name, bank, account number)",
  ],
  review: "Requests are reviewed within 3–5 business days.",
  charges: "A small processing charge may apply and will be deducted from the refunded amount where applicable (e.g. payment-gateway fees).",
  rejections: [
    "The funds have already been used for airtime, data or transfers",
    "The bank account does not match the originating payment",
    "The original transaction cannot be verified",
    "The request is a duplicate or the amount exceeds the unused balance",
  ],
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: ["bg-amber-100 text-amber-700", "Under review"],
    approved: ["bg-emerald-100 text-emerald-700", "Approved"],
    rejected: ["bg-red-100 text-red-700", "Rejected"],
  };
  const [cls, label] = map[status] || ["bg-slate-100 text-slate-600", status];
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
};

export const RefundPage = ({ onBack }) => {
  const [tab, setTab] = useState("request");
  const [fundings, setFundings] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    original_reference: "", amount: "", bank_name: "", account_number: "", account_name: "", reason: "",
  });

  const load = () => {
    api.get("/wallet/transactions", { params: { category: "fund_add", limit: 50 } })
      .then((r) => setFundings(r.data.transactions || [])).catch(() => {});
    api.get("/refunds/me").then((r) => setRefunds(r.data.refunds || [])).catch(() => {});
  };
  useEffect(load, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.original_reference) return toast.error("Select the funding transaction to refund");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid amount");
    if (!form.bank_name || !form.account_number || !form.account_name) return toast.error("Enter your bank account details");
    setLoading(true);
    try {
      await api.post("/refunds", { ...form, amount: Number(form.amount) });
      toast.success("Refund request submitted — we'll review it within 3–5 business days");
      setForm({ original_reference: "", amount: "", bank_name: "", account_number: "", account_name: "", reason: "" });
      setTab("history");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit your request");
    } finally { setLoading(false); }
  };

  const inputCls = "flex w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 h-12 px-4 text-base outline-none transition-all";

  return (
    <div className="min-h-screen bg-background-light" data-testid="refund-page">
      <header className="bg-primary-custom text-white sticky top-0 z-50">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={onBack} data-testid="refund-back" className="p-2 hover:bg-white/10 rounded-lg transition-colors"><Icon name="arrow_back" /></button>
          <span className="font-bold text-xl">Refunds</span>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-5 pb-24">
        <div className="flex bg-white rounded-xl p-1.5 gap-1">
          <button onClick={() => setTab("request")} data-testid="refund-tab-request" className={`flex-1 py-3 rounded-lg font-semibold transition-all ${tab === "request" ? "bg-primary-custom text-white" : "text-slate-600 hover:bg-slate-100"}`}>Request</button>
          <button onClick={() => setTab("history")} data-testid="refund-tab-history" className={`flex-1 py-3 rounded-lg font-semibold transition-all ${tab === "history" ? "bg-primary-custom text-white" : "text-slate-600 hover:bg-slate-100"}`}>My Requests</button>
          <button onClick={() => setTab("policy")} data-testid="refund-tab-policy" className={`flex-1 py-3 rounded-lg font-semibold transition-all ${tab === "policy" ? "bg-primary-custom text-white" : "text-slate-600 hover:bg-slate-100"}`}>Policy</button>
        </div>

        {tab === "request" && (
          <form onSubmit={submit} className="bg-white rounded-2xl p-6 space-y-4" data-testid="refund-form">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
              Refunds are only for <b>accidental excess funding</b> and are returned to your <b>originating bank account</b>. Only unused funds qualify.
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Original funding transaction</label>
              <select value={form.original_reference} onChange={set("original_reference")} data-testid="refund-reference" className={inputCls} required>
                <option value="">Select the payment to refund</option>
                {fundings.map((t) => (
                  <option key={t.reference || t.id} value={t.reference || ""}>
                    {naira(t.amount)} · {new Date(t.timestamp).toLocaleDateString()} {t.reference ? `· ${t.reference}` : ""}
                  </option>
                ))}
              </select>
              {fundings.length === 0 && <p className="text-xs text-slate-400">No wallet funding transactions found.</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Excess amount to refund (₦)</label>
              <input type="number" min="1" step="0.01" value={form.amount} onChange={set("amount")} data-testid="refund-amount" className={inputCls} placeholder="e.g. 5000" required />
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-slate-700 text-sm font-semibold">Bank name</label>
                <input value={form.bank_name} onChange={set("bank_name")} data-testid="refund-bank" className={inputCls} placeholder="e.g. GTBank" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-slate-700 text-sm font-semibold">Account number</label>
                  <input value={form.account_number} onChange={set("account_number")} data-testid="refund-account-number" className={inputCls} placeholder="0123456789" required />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-slate-700 text-sm font-semibold">Account name</label>
                  <input value={form.account_name} onChange={set("account_name")} data-testid="refund-account-name" className={inputCls} placeholder="As on your bank account" required />
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-slate-700 text-sm font-semibold">Reason <span className="text-slate-400 font-normal">(optional)</span></label>
              <textarea value={form.reason} onChange={set("reason")} data-testid="refund-reason" rows={3} className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/20 px-4 py-3 text-base outline-none transition-all" placeholder="Tell us what happened" />
            </div>
            <button type="submit" disabled={loading} data-testid="refund-submit" className="w-full h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg shadow-lg shadow-primary-custom/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? <Icon name="progress_activity" className="animate-spin" /> : <><Icon name="send" /><span>Submit request</span></>}
            </button>
          </form>
        )}

        {tab === "history" && (
          <div className="space-y-3" data-testid="refund-history">
            {refunds.length === 0 && <div className="bg-white rounded-2xl p-8 text-center text-slate-400">No refund requests yet.</div>}
            {refunds.map((r) => (
              <div key={r.id} className="bg-white rounded-2xl p-4 border border-slate-100" data-testid={`refund-item-${r.id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-extrabold text-slate-900">{naira(r.amount)}</p>
                  <StatusBadge status={r.status} />
                </div>
                <p className="text-xs text-slate-500 mt-1">To {r.bank_name} ****{String(r.account_number).slice(-4)} · {new Date(r.created_at).toLocaleDateString()}</p>
                {r.reason && <p className="text-xs text-slate-500 mt-1">"{r.reason}"</p>}
                {r.admin_note && <p className="text-xs mt-2 text-slate-600 bg-slate-50 rounded-lg p-2"><b>Note:</b> {r.admin_note}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === "policy" && (
          <div className="bg-white rounded-2xl p-6 space-y-4 text-sm text-slate-600" data-testid="refund-policy">
            <h3 className="font-bold text-slate-900 text-base">Refund Policy</h3>
            <p>{POLICY.intro}</p>
            <div>
              <p className="font-semibold text-slate-800 mb-1">A refund is approved only when:</p>
              <ul className="list-disc pl-5 space-y-1">{POLICY.eligible.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
            <div>
              <p className="font-semibold text-slate-800 mb-1">Supporting documents required:</p>
              <ul className="list-disc pl-5 space-y-1">{POLICY.docs.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
            <p><b className="text-slate-800">Review period:</b> {POLICY.review}</p>
            <p><b className="text-slate-800">Processing charges:</b> {POLICY.charges}</p>
            <div>
              <p className="font-semibold text-slate-800 mb-1">A request may be rejected if:</p>
              <ul className="list-disc pl-5 space-y-1">{POLICY.rejections.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
